/**
 * TaskCard Component
 *
 * Displays a task card for use in Kanban board or list views.
 * Supports drag-and-drop when used with @dnd-kit.
 *
 * @module components/tasks/TaskCard
 */

import { memo, useCallback } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MessageCircle, Clock } from 'lucide-react';
import TaskStatusBadge from './TaskStatusBadge.jsx';
import TaskPriorityBadge from './TaskPriorityBadge.jsx';
import TaskLabelBadge from './TaskLabelBadge.jsx';
import { Avatar } from '../common/Avatar.jsx';

// ============================================
// Constants
// ============================================

/**
 * Maximum number of labels to show before truncating
 */
const MAX_VISIBLE_LABELS = 2;

// ============================================
// Main Component
// ============================================

/**
 * TaskCard Component
 *
 * @param {object} props - Component props
 * @param {object} props.task - Task object
 * @param {boolean} [props.isDragging=false] - Whether the card is being dragged
 * @param {boolean} [props.isOverlay=false] - Whether this is a drag overlay
 * @param {boolean} [props.isHighlighted=false] - Whether this card should be highlighted (for live updates)
 * @param {function} [props.onClick] - Click handler
 * @param {object} [props.style] - Inline styles (for virtualization positioning)
 * @param {string} [props.className=''] - Additional CSS classes
 * @returns {React.ReactElement} Card element
 */
const TaskCard = memo(function TaskCard({
  task,
  isDragging: isDraggingProp = false,
  isOverlay = false,
  isHighlighted = false,
  onClick,
  style,
  className = ''
}) {
  if (!task) return null;

  // Setup sortable for dnd-kit with smooth animations
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isDraggingDnd
  } = useSortable({
    id: task.id,
    disabled: isOverlay,
    // Smooth transition when items move to make room for dragged item
    transition: {
      duration: 250, // ms - slightly longer for smoother feel
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)' // Smooth deceleration
    }
  });

  const isDragging = isDraggingProp || isDraggingDnd;

  // Combine styles for smooth animated transforms
  // Note: When used inside virtualized lists, the parent passes a style with
  // position/transform for virtualization. We need to combine both transforms.
  const dragStyle = {
    ...style,
    // Use CSS.Translate instead of CSS.Transform to avoid scale issues
    // and combine with any existing transform from virtualization
    transform: transform
      ? style?.transform
        ? `${style.transform} ${CSS.Translate.toString(transform)}`
        : CSS.Translate.toString(transform)
      : style?.transform,
    transition: transition || undefined,
    // Ensure dragged item is on top
    zIndex: isDragging ? 50 : undefined,
    // Add will-change for GPU acceleration during drag
    willChange: isDragging ? 'transform' : undefined
  };

  const handleClick = useCallback((e) => {
    if (onClick) {
      e.stopPropagation();
      onClick(task);
    }
  }, [onClick, task]);

  const {
    title,
    priority,
    status,
    labels = [],
    assignee,
    assigneeType,
    commentCount = 0,
    estimatedMinutes
  } = task;

  // Truncate labels if too many
  const visibleLabels = labels.slice(0, MAX_VISIBLE_LABELS);
  const hiddenLabelCount = labels.length - MAX_VISIBLE_LABELS;

  return (
    <div
      ref={setNodeRef}
      className={`
        bg-gray-800 rounded-lg p-3 border border-gray-700
        hover:border-gray-600 transition-all duration-200
        ${onClick ? 'cursor-pointer' : 'cursor-grab'}
        ${isDragging ? 'opacity-50 cursor-grabbing' : ''}
        ${isOverlay ? 'shadow-xl ring-2 ring-indigo-500 rotate-2' : ''}
        ${isHighlighted ? 'ring-2 ring-emerald-500 shadow-lg shadow-emerald-500/30 animate-pulse' : ''}
        ${className}
      `}
      style={dragStyle}
      onClick={handleClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      {...listeners}
      {...attributes}
    >
      {/* Header: Priority + Labels */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <TaskPriorityBadge priority={priority} size="sm" iconOnly />
        {visibleLabels.map(label => (
          <TaskLabelBadge
            key={label.id || label.name}
            label={label}
            size="sm"
          />
        ))}
        {hiddenLabelCount > 0 && (
          <span className="text-xs text-gray-500">
            +{hiddenLabelCount}
          </span>
        )}
      </div>

      {/* Title */}
      <h4 className="text-sm font-medium text-gray-200 mb-2 line-clamp-2">
        {title}
      </h4>

      {/* Footer: Assignee, Comments, Time Estimate */}
      <div className="flex items-center justify-between text-gray-500">
        {/* Left side: Assignee */}
        <div className="flex items-center gap-1.5">
          {assignee ? (
            <>
              <Avatar
                name={assignee.displayName || assignee.name}
                avatarUrl={assignee.avatarUrl}
                type={assigneeType}
                size="xs"
              />
              <span className="text-xs text-gray-400 truncate max-w-[80px]">
                {assignee.displayName || assignee.name}
              </span>
            </>
          ) : (
            <span className="text-xs text-gray-600 italic">Unassigned</span>
          )}
        </div>

        {/* Right side: Comments + Time */}
        <div className="flex items-center gap-3">
          {estimatedMinutes > 0 && (
            <div className="flex items-center gap-1" title={`${estimatedMinutes} minutes estimated`}>
              <Clock className="w-3 h-3" />
              <span className="text-xs">
                {estimatedMinutes >= 60
                  ? `${Math.floor(estimatedMinutes / 60)}h`
                  : `${estimatedMinutes}m`}
              </span>
            </div>
          )}
          {commentCount > 0 && (
            <div className="flex items-center gap-1" title={`${commentCount} comments`}>
              <MessageCircle className="w-3 h-3" />
              <span className="text-xs">{commentCount}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default TaskCard;
